import {
  ParagraphOp,
  Snippet,
  SnippetItem,
  ShapeModification,
} from "../types";
import { CITI_COLORS, CITI_POSITIONS, CITI_THEME_COLORS } from "./ai/citiStyleConfig";
import {
  DEFAULT_SNIPPET_STYLE_PROFILE,
  type SnippetStyleProfile,
} from "./ai/snippetStyle";

const MARGIN = 40;
const TITLE_HEIGHT = 40;
const TITLE_TOP = 20;
const CONTENT_TOP = TITLE_TOP + TITLE_HEIGHT + 15;

let _themeAccent = "#2F8CFF";
let _useTextBoxesForStatGrid = false;
let _snippetProfile: SnippetStyleProfile = DEFAULT_SNIPPET_STYLE_PROFILE;
const DEFAULT_GREY = "#F5F7FA";
const DEFAULT_BORDER = "#CCCCCC";
const DEFAULT_TEXT = "#333333";

const DEFAULT_ACCENT = "#2F8CFF";
const MIN_SHAPE_SIZE = 1;
const MIN_TEXT_WIDTH = 8;
const MIN_TEXT_HEIGHT = 16;
const COMPACT_HEADER_HEIGHT = 22;
const COMPACT_PADDING = 6;
const ACCENT_STRIP_HEIGHT = 4.5;

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[r, g, b].map((c) => clamp(c).toString(16).padStart(2, "0")).join("")}`;
}

function shadeColor(hex: string, factor: number): string {
  const [r, g, b] = hexToRgb(hex);
  if (factor > 0) {
    return rgbToHex(r + (255 - r) * factor, g + (255 - g) * factor, b + (255 - b) * factor);
  }
  return rgbToHex(r * (1 + factor), g * (1 + factor), b * (1 + factor));
}

function accentShades(base: string, count: number): string[] {
  if (count <= 1) return [base];
  const shades: string[] = [];
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0 : i / (count - 1);
    const factor = -0.3 + t * 0.6;
    shades.push(shadeColor(base, factor));
  }
  return shades;
}

function currentProfile(): SnippetStyleProfile {
  return _snippetProfile;
}

function usesSaturatedHeader(): boolean {
  return currentProfile().headerMode === "saturatedBar";
}

function profileSurfaceFill(accent: string): string {
  const profile = currentProfile();
  switch (profile.surface) {
    case "accentFlood":
      return accent;
    case "lightGrey":
      return "#F5F5F5";
    case "pastel":
      return profile.pastelFill ?? "#ECEBE4";
    case "white":
    default:
      return "#FFFFFF";
  }
}

function profileHeadingTextColor(): string {
  return currentProfile().surface === "accentFlood" ? "#FFFFFF" : DEFAULT_TEXT;
}

function profileItemFill(accent: string, itemColor?: string): string {
  if (currentProfile().shadeItemsFromHeader) return itemColor || accent;
  return profileSurfaceFill(accent);
}

function packedStride(regionHeight: number, n: number, gap: number): number {
  const stretch = n > 0 ? (regionHeight - (n - 1) * gap) / n : regionHeight;
  const profile = currentProfile();
  if (!profile.packFromTop) return Math.max(stretch, MIN_TEXT_HEIGHT);
  const cap = profile.maxRowHeightPt ?? 64;
  return Math.max(MIN_TEXT_HEIGHT, Math.min(stretch, cap));
}

function pushAccentStrip(
  shapes: ShapeModification[],
  left: number,
  top: number,
  width: number,
  accent: string
): void {
  shapes.push({
    id: nextId(),
    action: "create",
    geometricType: "rectangle",
    left,
    top,
    width,
    height: ACCENT_STRIP_HEIGHT,
    fillColor: accent,
    lineWidth: 0,
  });
}

let _idCounter = 0;
function nextId(): string {
  return `bp_${++_idCounter}`;
}

function hasText(value: string | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function positiveSize(value: number, min = MIN_SHAPE_SIZE): number {
  return Number.isFinite(value) && value > min ? value : min;
}

function compactHeaderHeight(regionHeight: number, normalHeaderHeight: number): number {
  const normalMinimum = normalHeaderHeight + COMPACT_PADDING * 2 + MIN_TEXT_HEIGHT;
  if (regionHeight >= normalMinimum) return normalHeaderHeight;
  return Math.min(normalHeaderHeight, COMPACT_HEADER_HEIGHT);
}

function compactPadding(availableHeight: number, normalPadding: number): number {
  if (availableHeight >= normalPadding * 2 + MIN_TEXT_HEIGHT) return normalPadding;
  return Math.max(2, Math.min(COMPACT_PADDING, Math.floor((availableHeight - MIN_TEXT_HEIGHT) / 2)));
}

function compactFontSize(baseFontSize: number, compact: boolean, reduction = 2): number {
  return compact ? Math.max(9, baseFontSize - reduction) : baseFontSize;
}

function isLineLikeShape(shape: ShapeModification): boolean {
  const token = shape.geometricType?.toLowerCase() ?? "";
  return token.includes("line") || token.includes("connector") || token === "straight";
}

function sanitizeShapeBounds(shape: ShapeModification): ShapeModification {
  const next: ShapeModification = { ...shape };
  const lineLike = isLineLikeShape(shape);

  if (typeof next.width === "number" && (!Number.isFinite(next.width) || next.width <= 0)) {
    next.width = lineLike ? 0 : MIN_SHAPE_SIZE;
  }
  if (typeof next.height === "number" && (!Number.isFinite(next.height) || next.height <= 0)) {
    next.height = lineLike ? 0 : MIN_SHAPE_SIZE;
  }
  return next;
}

function sanitizeExpandedShapes(shapes: ShapeModification[]): ShapeModification[] {
  return shapes.map(sanitizeShapeBounds);
}

export interface BulletBlockInput {
  label?: string;
  description?: string;
  bullets?: string[];
}

export interface BulletBlockResult {
  text: string;
  paragraphOps?: ParagraphOp[];
}

export function buildBulletBlock(input: BulletBlockInput): BulletBlockResult {
  const paragraphs: Array<{ text: string | undefined; bullet: boolean }> = [
    { text: input.label, bullet: false },
    ...(input.bullets?.length
      ? input.bullets.map((text) => ({ text, bullet: true }))
      : [{ text: input.description, bullet: false }]),
  ];
  const normalized = paragraphs.filter((paragraph): paragraph is { text: string; bullet: boolean } =>
    hasText(paragraph.text)
  );
  const bulletIndices: number[] = [];
  const plainIndices: number[] = [];

  normalized.forEach((paragraph, index) => {
    if (paragraph.bullet) {
      bulletIndices.push(index);
    } else {
      plainIndices.push(index);
    }
  });
  const paragraphOps: NonNullable<ShapeModification["paragraphOps"]> = [
    ...(plainIndices.length && bulletIndices.length
      ? [
          {
            target: { type: "indices" as const, indices: plainIndices },
            set: { bullet: { visible: false, type: "None" as const } },
          },
        ]
      : []),
    ...(bulletIndices.length
      ? [
          {
            target: { type: "indices" as const, indices: bulletIndices },
            set: { bullet: { visible: true, type: "Unnumbered" as const } },
          },
        ]
      : []),
  ];

  return {
    text: normalized.map((paragraph) => paragraph.text).join("\r"),
    ...(paragraphOps.length ? { paragraphOps } : {}),
  };
}

function bulletTextsFromItems(items: SnippetItem[]): string[] {
  return items.flatMap((item) => {
    if (item.bullets?.length) return item.bullets;
    if (hasText(item.description)) return [item.description];
    if (hasText(item.label)) return [item.label];
    return [];
  });
}

function pushGeometricTextBlock(
  shapes: ShapeModification[],
  geometricProps: Partial<ShapeModification>,
  block: BulletBlockResult,
  textProps: Partial<ShapeModification> = {},
  options: { inset?: number } = {}
): void {
  if (!block.text) return;

  if (block.paragraphOps?.length) {
    const inset = options.inset ?? 0;
    const textBounds = {
      left: typeof geometricProps.left === "number" ? geometricProps.left + inset : geometricProps.left,
      top: typeof geometricProps.top === "number" ? geometricProps.top + inset : geometricProps.top,
      width: typeof geometricProps.width === "number" ? positiveSize(geometricProps.width - inset * 2, MIN_TEXT_WIDTH) : geometricProps.width,
      height: typeof geometricProps.height === "number" ? positiveSize(geometricProps.height - inset * 2, MIN_TEXT_HEIGHT) : geometricProps.height,
    };

    shapes.push({
      id: nextId(),
      action: "create",
      ...geometricProps,
    });

    shapes.push({
      id: nextId(),
      action: "create",
      text: block.text,
      paragraphOps: block.paragraphOps,
      ...textBounds,
      ...textProps,
    });
    return;
  }

  shapes.push({
    id: nextId(),
    action: "create",
    ...geometricProps,
    text: block.text,
    ...textProps,
  });
}

interface Region {
  left: number;
  top: number;
  width: number;
  height: number;
}

function getRegion(snippet: Snippet, slideWidth: number, slideHeight: number): Region {
  return {
    left: snippet.left ?? MARGIN,
    top: snippet.top ?? (snippet.title ? CONTENT_TOP : TITLE_TOP),
    width: snippet.width ?? (slideWidth - (snippet.left ?? MARGIN) - MARGIN),
    height: snippet.height ?? (slideHeight - (snippet.top ?? (snippet.title ? CONTENT_TOP : TITLE_TOP)) - 30),
  };
}

function titleShape(
  title: string,
  snippet: Snippet,
  slideWidth: number
): ShapeModification {
  return {
    id: nextId(),
    action: "create",
    text: title,
    left: snippet.left ?? MARGIN,
    top: snippet.top ? Math.max(snippet.top - TITLE_HEIGHT - 10, TITLE_TOP) : TITLE_TOP,
    width: snippet.width ?? (slideWidth - MARGIN * 2),
    height: TITLE_HEIGHT,
    fontSize: snippet.fontSize ? snippet.fontSize + 6 : 20,
    bold: true,
    textColor: snippet.textColor ?? DEFAULT_TEXT,
    fontFamily: snippet.fontFamily,
  };
}

function processFlowLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 7);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const arrowWidth = 16;
  const totalArrowWidth = (n - 1) * arrowWidth;
  const gapBetween = 8;
  const totalGaps = (n - 1) * gapBetween;
  const boxWidth = Math.min(
    160,
    (region.width - totalArrowWidth - totalGaps) / n
  );
  const boxHeight = Math.min(80, packedStride(region.height, 1, 0), region.height);
  const boxTop = region.top;
  const totalWidth = n * boxWidth + totalArrowWidth + totalGaps;
  const startX = region.left + (currentProfile().packFromTop ? 0 : Math.max(0, (region.width - totalWidth) / 2));
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const surface = profileSurfaceFill(accent);
  const headingColor = profileHeadingTextColor();

  items.forEach((item, i) => {
    const x = startX + i * (boxWidth + arrowWidth + gapBetween);
    const fill = usesSaturatedHeader()
      ? item.color || (i === 0 ? DEFAULT_ACCENT : DEFAULT_GREY)
      : surface;
    const textColor = usesSaturatedHeader()
      ? (i === 0 ? "#FFFFFF" : DEFAULT_TEXT)
      : headingColor;

    if (!usesSaturatedHeader()) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: x,
        top: boxTop,
        width: boxWidth,
        height: boxHeight,
        fillColor: fill,
        lineColor: DEFAULT_BORDER,
        lineWidth: 0.75,
      });
      pushAccentStrip(shapes, x, boxTop, boxWidth, accent);
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: x + 6,
        top: boxTop + ACCENT_STRIP_HEIGHT + 4,
        width: positiveSize(boxWidth - 12, MIN_TEXT_WIDTH),
        height: positiveSize(boxHeight - ACCENT_STRIP_HEIGHT - 8, MIN_TEXT_HEIGHT),
        textColor,
        fontSize: 14,
        bold: false,
        verticalAlignment: "middle",
      });
    } else {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: x,
        top: boxTop,
        width: boxWidth,
        height: boxHeight,
        fillColor: fill,
        lineColor: DEFAULT_BORDER,
        lineWidth: 0.75,
        text: item.label,
        textColor,
        fontSize: 14,
        bold: false,
      });
    }

    if (item.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.description,
        left: x,
        top: boxTop + boxHeight + 6,
        width: boxWidth,
        height: 50,
        fontSize: 10,
        textColor: "#666666",
        wordWrap: true,
      });
    }

    if (i < n - 1) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "arrow",
        left: x + boxWidth + gapBetween / 2 - arrowWidth / 2 + 2,
        top: boxTop + boxHeight / 2 - 8,
        width: arrowWidth,
        height: 16,
        fillColor: "#999999",
        lineWidth: 0,
      });
    }
  });

  return shapes;
}

function funnelLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 6);
  const n = items.length;
  if (n === 0) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const areaHeight = slideHeight - CONTENT_TOP - MARGIN;
  const maxWidth = slideWidth * 0.65;
  const minWidth = maxWidth * 0.3;
  const rowHeight = Math.min(50, (areaHeight - (n - 1) * 4) / n);
  const totalHeight = n * rowHeight + (n - 1) * 4;
  const startY = CONTENT_TOP + (areaHeight - totalHeight) / 2;

  items.forEach((item, i) => {
    const ratio = 1 - (i / Math.max(n - 1, 1)) * 0.7;
    const w = minWidth + (maxWidth - minWidth) * ratio;
    const x = (slideWidth - w) / 2;
    const y = startY + i * (rowHeight + 4);

    const opacity = 1 - i * 0.12;
    const baseColor = item.color || DEFAULT_ACCENT;
    const fillColor = i === 0 ? baseColor : blendWithWhite(baseColor, opacity);

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: x,
      top: y,
      width: w,
      height: rowHeight,
      fillColor,
      lineWidth: 0,
      text: item.label,
      textColor: i === 0 ? "#FFFFFF" : DEFAULT_TEXT,
      fontSize: 14,
    });

    if (item.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.description,
        left: (slideWidth + w) / 2 + 12,
        top: y + 4,
        width: slideWidth - (slideWidth + w) / 2 - 12 - MARGIN,
        height: rowHeight - 8,
        fontSize: 14,
        textColor: "#666666",
      });
    }
  });

  return shapes;
}

function pyramidLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 5);
  const n = items.length;
  if (n === 0) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const areaHeight = slideHeight - CONTENT_TOP - MARGIN;
  const maxWidth = slideWidth * 0.6;
  const minWidth = maxWidth * 0.25;
  const rowHeight = Math.min(55, (areaHeight - (n - 1) * 3) / n);
  const totalHeight = n * rowHeight + (n - 1) * 3;
  const startY = CONTENT_TOP + (areaHeight - totalHeight) / 2;

  items.forEach((item, i) => {
    const ratio = i / Math.max(n - 1, 1);
    const w = minWidth + (maxWidth - minWidth) * ratio;
    const x = (slideWidth - w) / 2;
    const y = startY + i * (rowHeight + 3);

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: x,
      top: y,
      width: w,
      height: rowHeight,
      fillColor: item.color || blendWithWhite(DEFAULT_ACCENT, 1 - i * 0.15),
      lineWidth: 0,
      text: item.label,
      textColor: i === 0 ? "#FFFFFF" : DEFAULT_TEXT,
      fontSize: 14,
    });
  });

  return shapes;
}

function cycleDiagramLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 6);
  const n = items.length;
  if (n === 0) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const areaHeight = slideHeight - CONTENT_TOP - MARGIN;
  const centerX = slideWidth / 2;
  const centerY = CONTENT_TOP + areaHeight / 2;
  const radius = Math.min(areaHeight, slideWidth - MARGIN * 2) * 0.32;
  const boxSize = Math.min(90, radius * 0.9);

  items.forEach((item, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    const x = centerX + radius * Math.cos(angle) - boxSize / 2;
    const y = centerY + radius * Math.sin(angle) - boxSize / 2;

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: x,
      top: y,
      width: boxSize,
      height: boxSize,
      fillColor: item.color || DEFAULT_ACCENT,
      lineWidth: 0,
      text: item.label,
      textColor: "#FFFFFF",
      fontSize: 14,
    });

    const nextAngle = (2 * Math.PI * ((i + 1) % n)) / n - Math.PI / 2;
    const midAngle = (angle + nextAngle + (nextAngle < angle ? 2 * Math.PI : 0)) / 2;
    const arrowR = radius * 0.72;
    const ax = centerX + arrowR * Math.cos(midAngle) - 6;
    const ay = centerY + arrowR * Math.sin(midAngle) - 6;

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "ellipse",
      left: ax,
      top: ay,
      width: 12,
      height: 12,
      fillColor: "#999999",
      lineWidth: 0,
    });
  });

  return shapes;
}

function kpiDashboardLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 8);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const cols = n <= 3 ? n : n <= 4 ? 2 : n <= 6 ? 3 : 4;
  const rows = Math.ceil(n / cols);
  const gap = 12;
  const cardWidth = (region.width - (cols - 1) * gap) / cols;
  const cardHeight = packedStride(region.height, rows, gap);
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const saturated = usesSaturatedHeader();

  items.forEach((item, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = region.left + col * (cardWidth + gap);
    const y = region.top + row * (cardHeight + gap);

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: x,
      top: y,
      width: cardWidth,
      height: cardHeight,
      fillColor: saturated ? DEFAULT_GREY : profileSurfaceFill(accent),
      lineColor: DEFAULT_BORDER,
      lineWidth: 0.75,
    });
    if (!saturated) {
      pushAccentStrip(shapes, x, y, cardWidth, accent);
    }

    shapes.push({
      id: nextId(),
      action: "create",
      text: item.label,
      left: x + 10,
      top: y + (saturated ? 8 : ACCENT_STRIP_HEIGHT + 6),
      width: cardWidth - 20,
      height: 30,
      fontSize: 22,
      bold: true,
      textColor: saturated ? (item.color || DEFAULT_ACCENT) : DEFAULT_TEXT,
    });

    if (item.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.description,
        left: x + 10,
        top: y + 40,
        width: cardWidth - 20,
        height: cardHeight - 50,
        fontSize: 14,
        textColor: "#666666",
      });
    }
  });

  return shapes;
}

function orgChartLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 7);
  const n = items.length;
  if (n === 0) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const areaWidth = slideWidth - MARGIN * 2;
  const areaHeight = slideHeight - CONTENT_TOP - MARGIN;
  const boxWidth = Math.min(150, areaWidth * 0.2);
  const boxHeight = 50;

  if (n === 1) {
    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: (slideWidth - boxWidth) / 2,
      top: CONTENT_TOP + areaHeight / 2 - boxHeight / 2,
      width: boxWidth,
      height: boxHeight,
      fillColor: items[0].color || DEFAULT_ACCENT,
      lineWidth: 0,
      text: items[0].label,
      textColor: "#FFFFFF",
      fontSize: 14,
    });
    return shapes;
  }

  const rootX = (slideWidth - boxWidth) / 2;
  const rootY = CONTENT_TOP + 10;
  shapes.push({
    id: nextId(),
    action: "create",
    geometricType: "rectangle",
    left: rootX,
    top: rootY,
    width: boxWidth,
    height: boxHeight,
    fillColor: items[0].color || DEFAULT_ACCENT,
    lineWidth: 0,
    text: items[0].label,
    textColor: "#FFFFFF",
    fontSize: 14,
  });

  const children = items.slice(1);
  const childCount = children.length;
  const childGap = 12;
  const childBoxWidth = Math.min(
    boxWidth,
    (areaWidth - (childCount - 1) * childGap) / childCount
  );
  const totalChildWidth =
    childCount * childBoxWidth + (childCount - 1) * childGap;
  const childStartX = MARGIN + (areaWidth - totalChildWidth) / 2;
  const childY = rootY + boxHeight + 50;

  // Vertical connector from root
  shapes.push({
    id: nextId(),
    action: "create",
    geometricType: "line",
    left: slideWidth / 2,
    top: rootY + boxHeight,
    width: 0,
    height: 20,
    lineColor: DEFAULT_BORDER,
    lineWidth: 1,
  });

  // Horizontal connector bar
  if (childCount > 1) {
    const barLeft = childStartX + childBoxWidth / 2;
    const barRight =
      childStartX + (childCount - 1) * (childBoxWidth + childGap) + childBoxWidth / 2;
    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "line",
      left: barLeft,
      top: rootY + boxHeight + 20,
      width: barRight - barLeft,
      height: 0,
      lineColor: DEFAULT_BORDER,
      lineWidth: 1,
    });
  }

  children.forEach((child, i) => {
    const cx = childStartX + i * (childBoxWidth + childGap);
    const connectorX = cx + childBoxWidth / 2;

    // Vertical drop to child
    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "line",
      left: connectorX,
      top: rootY + boxHeight + 20,
      width: 0,
      height: childY - (rootY + boxHeight + 20),
      lineColor: DEFAULT_BORDER,
      lineWidth: 1,
    });

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: cx,
      top: childY,
      width: childBoxWidth,
      height: boxHeight,
      fillColor: child.color || DEFAULT_GREY,
      lineColor: DEFAULT_BORDER,
      lineWidth: 0.75,
      text: child.label,
      textColor: DEFAULT_TEXT,
      fontSize: 14,
    });

    if (child.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: child.description,
        left: cx,
        top: childY + boxHeight + 4,
        width: childBoxWidth,
        height: 25,
        fontSize: 14,
        textColor: "#666666",
      });
    }
  });

  return shapes;
}

function blendWithWhite(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const blend = (c: number) =>
    Math.round(c * opacity + 255 * (1 - opacity));
  return `#${blend(r).toString(16).padStart(2, "0")}${blend(g).toString(16).padStart(2, "0")}${blend(b).toString(16).padStart(2, "0")}`;
}

function matrixGridLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 4);
  if (items.length === 0) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const labelWidth = 30;
  const labelHeight = 25;
  const areaWidth = slideWidth - MARGIN * 2 - labelWidth;
  const areaHeight = slideHeight - CONTENT_TOP - MARGIN - labelHeight;
  const gap = 8;
  const cellW = (areaWidth - gap) / 2;
  const cellH = (areaHeight - gap) / 2;
  const gridLeft = MARGIN + labelWidth;
  const gridTop = CONTENT_TOP;

  const positions = [
    { col: 0, row: 0 },
    { col: 1, row: 0 },
    { col: 0, row: 1 },
    { col: 1, row: 1 },
  ];

  const quadrantColors = ["#E8F4FD", "#F0F7E8", "#FFF8E1", "#FDEAEA"];

  items.forEach((item, i) => {
    const pos = positions[i];
    const x = gridLeft + pos.col * (cellW + gap);
    const y = gridTop + pos.row * (cellH + gap);

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: x,
      top: y,
      width: cellW,
      height: cellH,
      fillColor: item.color || quadrantColors[i],
      lineColor: DEFAULT_BORDER,
      lineWidth: 0.75,
    });

    shapes.push({
      id: nextId(),
      action: "create",
      text: item.label,
      left: x + 10,
      top: y + 8,
      width: cellW - 20,
      height: 22,
      fontSize: 14,
      bold: true,
      textColor: DEFAULT_TEXT,
    });

    if (item.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.description,
        left: x + 10,
        top: y + 32,
        width: cellW - 20,
        height: cellH - 42,
        fontSize: 14,
        textColor: "#666666",
      });
    }
  });

  if (layout.axisLabels?.y) {
    shapes.push({
      id: nextId(),
      action: "create",
      text: layout.axisLabels.y,
      left: MARGIN - 5,
      top: gridTop + areaHeight / 2 - 40,
      width: labelWidth + 5,
      height: 80,
      fontSize: 12,
      bold: true,
      textColor: "#999999",
    });
  }

  if (layout.axisLabels?.x) {
    shapes.push({
      id: nextId(),
      action: "create",
      text: layout.axisLabels.x,
      left: gridLeft,
      top: gridTop + areaHeight + 4,
      width: areaWidth,
      height: labelHeight,
      fontSize: 12,
      bold: true,
      textColor: "#999999",
    });
  }

  return shapes;
}

function comparisonColumnsLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 4);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const gap = 12;
  const colWidth = (region.width - (n - 1) * gap) / n;
  const rowH = packedStride(region.height, 1, 0);
  const saturated = usesSaturatedHeader();
  const headerHeight = saturated ? 36 : Math.max(16, Math.min(24, rowH * 0.3));
  const accent = layout.headerColor ?? DEFAULT_ACCENT;

  items.forEach((item, i) => {
    const x = region.left + i * (colWidth + gap);

    if (saturated) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: x,
        top: region.top,
        width: colWidth,
        height: headerHeight,
        fillColor: item.color || accent,
        lineWidth: 0,
        text: item.label,
        textColor: "#FFFFFF",
        fontSize: 14,
        bold: true,
        verticalAlignment: "middle",
      });
    } else {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: x,
        top: region.top,
        width: colWidth,
        height: rowH,
        fillColor: profileSurfaceFill(accent),
        lineWidth: 0,
      });
      pushAccentStrip(shapes, x, region.top, colWidth, accent);
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: x + 8,
        top: region.top + ACCENT_STRIP_HEIGHT + 2,
        width: positiveSize(colWidth - 16, MIN_TEXT_WIDTH),
        height: headerHeight,
        fontSize: 14,
        bold: true,
        textColor: DEFAULT_TEXT,
        verticalAlignment: "middle",
      });
    }

    const bodyText = buildBulletBlock({
      bullets: item.bullets,
      description: item.description,
    });

    if (bodyText.text) {
      if (saturated) {
        shapes.push({
          id: nextId(),
          action: "create",
          geometricType: "rectangle",
          left: x,
          top: region.top + headerHeight,
          width: colWidth,
          height: rowH - headerHeight,
          fillColor: DEFAULT_GREY,
          lineColor: DEFAULT_BORDER,
          lineWidth: 0.75,
        });
      }

      shapes.push({
        id: nextId(),
        action: "create",
        ...bodyText,
        left: x + 8,
        top: region.top + headerHeight + 8,
        width: colWidth - 16,
        height: positiveSize(rowH - headerHeight - 16, MIN_TEXT_HEIGHT),
        fontSize: 14,
        textColor: DEFAULT_TEXT,
      });
    }
  });

  return shapes;
}

function waterfallBreakdownLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 8);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const barHeight = Math.min(60, region.height * 0.35);
  const barTop = region.top + (region.height - barHeight) / 2 - 15;

  const totalValue = items.reduce((sum, it) => sum + Math.abs(it.value || 1), 0);
  const minSeg = 36;
  const minTotal = minSeg * n;
  const flexWidth = Math.max(0, region.width - minTotal);

  let currentX = region.left;
  const palette = ["#2F8CFF", "#1B6FD1", "#145AAB", "#0E4585", "#D4762C", "#B85E1A", "#333333", "#666666"];

  items.forEach((item, i) => {
    const proportion = Math.abs(item.value || 1) / totalValue;
    const segWidth = minSeg + flexWidth * proportion;

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: currentX,
      top: barTop,
      width: segWidth,
      height: barHeight,
      fillColor: item.color || palette[i % palette.length],
      lineColor: "#FFFFFF",
      lineWidth: 1,
      text: item.label,
      textColor: "#FFFFFF",
      fontSize: segWidth > 80 ? 14 : 10,
      verticalAlignment: "middle",
    });

    if (item.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.description,
        left: currentX,
        top: barTop + barHeight + 6,
        width: segWidth,
        height: 22,
        fontSize: 12,
        textColor: "#666666",
      });
    }

    currentX += segWidth;
  });

  return shapes;
}

function iconStatRowLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 5);
  const n = items.length;
  if (n === 0) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const areaWidth = slideWidth - MARGIN * 2;
  const areaHeight = slideHeight - CONTENT_TOP - MARGIN;
  const cardWidth = areaWidth / n;
  const cardTop = CONTENT_TOP + (areaHeight - 80) / 2;

  items.forEach((item, i) => {
    const x = MARGIN + i * cardWidth;

    shapes.push({
      id: nextId(),
      action: "create",
      text: item.description || "",
      left: x + 10,
      top: cardTop,
      width: cardWidth - 20,
      height: 40,
      fontSize: 28,
      bold: true,
      textColor: item.color || DEFAULT_ACCENT,
    });

    shapes.push({
      id: nextId(),
      action: "create",
      text: item.label,
      left: x + 10,
      top: cardTop + 42,
      width: cardWidth - 20,
      height: 25,
      fontSize: 12,
      textColor: "#666666",
    });

    if (i < n - 1) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "line",
        left: x + cardWidth,
        top: cardTop + 5,
        width: 0,
        height: 55,
        lineColor: "#DDDDDD",
        lineWidth: 0.75,
      });
    }
  });

  return shapes;
}

function swimLanesLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 4);
  const n = items.length;
  if (n === 0) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const labelWidth = 120;
  const areaWidth = slideWidth - MARGIN * 2 - labelWidth;
  const areaHeight = slideHeight - CONTENT_TOP - MARGIN;
  const laneGap = 6;
  const laneHeight = (areaHeight - (n - 1) * laneGap) / n;
  const contentLeft = MARGIN + labelWidth;

  items.forEach((item, i) => {
    const y = CONTENT_TOP + i * (laneHeight + laneGap);

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: MARGIN,
      top: y,
      width: labelWidth - 4,
      height: laneHeight,
      fillColor: item.color || DEFAULT_ACCENT,
      lineWidth: 0,
      text: item.label,
      textColor: "#FFFFFF",
      fontSize: 14,
      bold: true,
      verticalAlignment: "middle",
    });

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: contentLeft,
      top: y,
      width: areaWidth,
      height: laneHeight,
      fillColor: i % 2 === 0 ? "#FAFBFC" : "#FFFFFF",
      lineColor: "#E5E5E5",
      lineWidth: 0.5,
    });

    if (item.bullets?.length) {
      shapes.push({
        id: nextId(),
        action: "create",
        ...buildBulletBlock({ bullets: item.bullets }),
        left: contentLeft + 10,
        top: y + 6,
        width: areaWidth - 20,
        height: laneHeight - 12,
        fontSize: 14,
        textColor: DEFAULT_TEXT,
      });
    } else if (item.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.description,
        left: contentLeft + 10,
        top: y + 6,
        width: areaWidth - 20,
        height: laneHeight - 12,
        fontSize: 14,
        textColor: DEFAULT_TEXT,
      });
    }
  });

  return shapes;
}

function beforeAfterLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const before = layout.items[0];
  const after = layout.items[1];
  if (!before || !after) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const areaWidth = slideWidth - MARGIN * 2;
  const areaHeight = slideHeight - CONTENT_TOP - MARGIN;
  const panelGap = 30;
  const panelWidth = (areaWidth - panelGap) / 2;
  const headerHeight = 32;
  const leftX = MARGIN;
  const rightX = MARGIN + panelWidth + panelGap;

  const panels = [
    { item: before, x: leftX, headerColor: "#E0E0E0", headerText: "#555555", bodyFill: "#F8F8F8", tag: "Before" },
    { item: after, x: rightX, headerColor: DEFAULT_ACCENT, headerText: "#FFFFFF", bodyFill: "#EDF5FF", tag: "After" },
  ];

  panels.forEach((p) => {
    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: p.x,
      top: CONTENT_TOP,
      width: panelWidth,
      height: headerHeight,
      fillColor: p.headerColor,
      lineWidth: 0,
      text: `${p.tag}: ${p.item.label}`,
      textColor: p.headerText,
      fontSize: 14,
      bold: true,
      verticalAlignment: "middle",
    });

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: p.x,
      top: CONTENT_TOP + headerHeight,
      width: panelWidth,
      height: areaHeight - headerHeight,
      fillColor: p.bodyFill,
      lineColor: DEFAULT_BORDER,
      lineWidth: 0.75,
    });

    const bodyText = buildBulletBlock({
      bullets: p.item.bullets,
      description: p.item.description,
    });

    if (bodyText.text) {
      shapes.push({
        id: nextId(),
        action: "create",
        ...bodyText,
        left: p.x + 12,
        top: CONTENT_TOP + headerHeight + 10,
        width: panelWidth - 24,
        height: areaHeight - headerHeight - 20,
        fontSize: 14,
        textColor: DEFAULT_TEXT,
      });
    }
  });

  // Arrow between panels
  const arrowX = MARGIN + panelWidth + (panelGap - 20) / 2;
  const arrowY = CONTENT_TOP + areaHeight / 2 - 10;
  shapes.push({
    id: nextId(),
    action: "create",
    geometricType: "arrow",
    left: arrowX,
    top: arrowY,
    width: 20,
    height: 20,
    fillColor: DEFAULT_ACCENT,
    lineWidth: 0,
  });

  return shapes;
}

function chevronBannerLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 7);
  const n = items.length;
  if (n === 0) return shapes;

  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const areaWidth = slideWidth - MARGIN * 2;
  const areaHeight = slideHeight - CONTENT_TOP - MARGIN;
  const chevronHeight = Math.min(50, areaHeight * 0.35);
  const overlap = 8;
  const chevronWidth = (areaWidth + overlap * (n - 1)) / n;
  const chevronTop = CONTENT_TOP + (areaHeight - chevronHeight) / 2 - 10;

  items.forEach((item, i) => {
    const x = MARGIN + i * (chevronWidth - overlap);
    const opacity = 1 - i * (0.6 / Math.max(n - 1, 1));
    const baseColor = item.color || DEFAULT_ACCENT;
    const fillColor = i === 0 ? baseColor : blendWithWhite(baseColor, opacity);

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: x,
      top: chevronTop,
      width: chevronWidth,
      height: chevronHeight,
      fillColor,
      lineColor: "#FFFFFF",
      lineWidth: 2,
      text: item.label,
      textColor: i < 2 ? "#FFFFFF" : DEFAULT_TEXT,
      fontSize: 14,
      bold: i === 0,
      verticalAlignment: "middle",
    });

    if (item.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.description,
        left: x,
        top: chevronTop + chevronHeight + 6,
        width: chevronWidth,
        height: 25,
        fontSize: 14,
        textColor: "#666666",
      });
    }
  });

  return shapes;
}

function statGridLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 12);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const cols = n <= 2 ? n : n <= 4 ? 2 : n <= 6 ? 2 : 3;
  const rows = Math.ceil(n / cols);
  const gap = 10;
  const cardW = (region.width - (cols - 1) * gap) / cols;
  const cardH = packedStride(region.height, rows, gap);
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const saturated = usesSaturatedHeader();

  items.forEach((item, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = region.left + col * (cardW + gap);
    const y = region.top + row * (cardH + gap);
    const itemColor = profileItemFill(accent, item.color);
    const baseFontSize = item.fontSize ?? layout.fontSize ?? 16;
    const textColor = saturated
      ? (item.textColor ?? "#FFFFFF")
      : (item.textColor ?? DEFAULT_TEXT);

    if (!saturated) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: x,
        top: y,
        width: cardW,
        height: cardH,
        fillColor: itemColor,
        lineWidth: 0,
      });
      pushAccentStrip(shapes, x, y, cardW, accent);
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: x + 8,
        top: y + ACCENT_STRIP_HEIGHT + 6,
        width: cardW - 16,
        height: 20,
        fontSize: baseFontSize,
        bold: false,
        textColor,
        fontFamily: layout.fontFamily,
        verticalAlignment: "top",
      });
      if (item.description) {
        shapes.push({
          id: nextId(),
          action: "create",
          text: item.description,
          left: x + 8,
          top: y + ACCENT_STRIP_HEIGHT + 26,
          width: cardW - 16,
          height: positiveSize(cardH - ACCENT_STRIP_HEIGHT - 34, MIN_TEXT_HEIGHT),
          fontSize: baseFontSize + 10,
          bold: item.bold ?? true,
          textColor,
          fontFamily: layout.fontFamily,
          verticalAlignment: "top",
        });
      }
      return;
    }

    if (_useTextBoxesForStatGrid) {
      const labelInset = 8;
      const labelTopInset = 8;
      const labelHeight = Math.round(
        Math.min(Math.max(20, baseFontSize + 4), Math.max(20, cardH * 0.35))
      );
      const valueTopOffset = Math.round(
        Math.min(Math.max(28, labelHeight + labelTopInset), Math.max(28, cardH - 28))
      );
      const valueHeight = Math.max(20, cardH - valueTopOffset - 8);

      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: x,
        top: y,
        width: cardW,
        height: cardH,
        fillColor: itemColor,
        lineWidth: 0,
      });

      // COM ignores top-aligned text inside geometric shapes, so render stat
      // labels and values as separate text boxes over the card background.
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: x + labelInset,
        top: y + labelTopInset,
        width: cardW - labelInset * 2,
        height: labelHeight,
        fontSize: baseFontSize,
        bold: false,
        textColor,
        fontFamily: layout.fontFamily,
        verticalAlignment: "top",
      });

      if (item.description) {
        shapes.push({
          id: nextId(),
          action: "create",
          text: item.description,
          left: x + labelInset,
          top: y + valueTopOffset,
          width: cardW - labelInset * 2,
          height: valueHeight,
          fontSize: baseFontSize + 10,
          bold: item.bold ?? true,
          textColor,
          fontFamily: layout.fontFamily,
          verticalAlignment: "top",
        });
      }
      return;
    }

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: x,
      top: y,
      width: cardW,
      height: cardH,
      fillColor: itemColor,
      lineWidth: 0,
      text: item.label,
      textColor,
      fontSize: baseFontSize,
      bold: false,
      verticalAlignment: "top",
    });

    if (item.description) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.description,
        left: x + 8,
        top: y + 24,
        width: cardW - 16,
        height: cardH - 32,
        fontSize: baseFontSize + 10,
        bold: item.bold ?? true,
        textColor,
      });
    }
  });

  return shapes;
}

function sectionBlockLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 6);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const normalHeaderH = 30;
  const gap = 12;
  const blockH = packedStride(region.height, n, gap);
  const isCompact = blockH < normalHeaderH + MIN_TEXT_HEIGHT + 20;
  const headerH = usesSaturatedHeader()
    ? (isCompact ? Math.min(normalHeaderH, COMPACT_HEADER_HEIGHT) : normalHeaderH)
    : Math.max(18, Math.min(normalHeaderH, blockH * 0.35));
  const inset = isCompact ? 4 : 8;
  const bodyH = positiveSize(blockH - headerH - 4, MIN_TEXT_HEIGHT + inset * 2);
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const baseFontSize = compactFontSize(layout.fontSize ?? 16, isCompact);
  const saturated = usesSaturatedHeader();

  items.forEach((item, i) => {
    const y = region.top + i * (blockH + gap);

    if (saturated) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left,
        top: y,
        width: region.width,
        height: headerH,
        fillColor: item.color || accent,
        lineColor: item.color || accent,
        lineWidth: 0,
        text: item.label,
        textColor: item.textColor ?? "#FFFFFF",
        fontSize: item.fontSize ?? baseFontSize,
        bold: item.bold ?? true,
        verticalAlignment: "middle",
      });
    } else {
      pushAccentStrip(shapes, region.left, y, region.width, accent);
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: region.left,
        top: y + ACCENT_STRIP_HEIGHT,
        width: region.width,
        height: headerH - ACCENT_STRIP_HEIGHT,
        fontSize: item.fontSize ?? baseFontSize,
        bold: item.bold ?? true,
        textColor: item.textColor ?? DEFAULT_TEXT,
        verticalAlignment: "middle",
      });
    }

    const bodyText = buildBulletBlock({
      bullets: item.bullets,
      description: item.description,
    });
    if (bodyText.text) {
      pushGeometricTextBlock(
        shapes,
        {
          geometricType: "rectangle",
          left: region.left,
          top: y + headerH,
          width: region.width,
          height: bodyH,
          fillColor: profileSurfaceFill(accent),
          lineColor: "#E0E0E0",
          lineWidth: 0.5,
        },
        bodyText,
        {
          fontSize: baseFontSize,
          textColor: layout.textColor ?? DEFAULT_TEXT,
          verticalAlignment: "top",
          wordWrap: true,
        },
        { inset }
      );
    }
  });

  return shapes;
}

function columnCardsLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 6);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const gap = 12;
  const normalHeaderH = 32;
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const baseFontSize = layout.fontSize ?? 16;

  const useGrid = n === 4 || n > 4;
  const cols = useGrid ? 2 : n;
  const rows = useGrid ? Math.ceil(n / 2) : 1;
  const colW = (region.width - (cols - 1) * gap) / cols;
  const rowH = packedStride(region.height, rows, gap);
  const compact = rowH < normalHeaderH + MIN_TEXT_HEIGHT + 16;
  const saturated = usesSaturatedHeader();
  const headerH = saturated
    ? (compact ? Math.max(10, Math.min(COMPACT_HEADER_HEIGHT, rowH * 0.45)) : normalHeaderH)
    : Math.max(16, Math.min(24, rowH * 0.3));
  const padX = compact ? 8 : 10;
  const padTop = compact ? 6 : 8;
  const bodyTextH = positiveSize(rowH - headerH - padTop * 2, MIN_TEXT_HEIGHT);
  const effectiveFontSize = compactFontSize(baseFontSize, compact, 3);

  items.forEach((item, i) => {
    const col = useGrid ? i % 2 : i;
    const row = useGrid ? Math.floor(i / 2) : 0;
    const x = region.left + col * (colW + gap);
    const y = region.top + row * (rowH + gap);

    if (saturated) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: x,
        top: y,
        width: colW,
        height: headerH,
        fillColor: item.color || accent,
        lineWidth: 0,
        text: item.label,
        textColor: item.textColor ?? "#FFFFFF",
        fontSize: item.fontSize ?? baseFontSize,
        bold: item.bold ?? true,
        verticalAlignment: "middle",
      });
    } else {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: x,
        top: y,
        width: colW,
        height: rowH,
        fillColor: profileSurfaceFill(accent),
        lineWidth: 0,
      });
      pushAccentStrip(shapes, x, y, colW, accent);
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: x + padX,
        top: y + ACCENT_STRIP_HEIGHT + 2,
        width: positiveSize(colW - padX * 2, MIN_TEXT_WIDTH),
        height: headerH,
        fontSize: item.fontSize ?? baseFontSize,
        bold: item.bold ?? true,
        textColor: item.textColor ?? DEFAULT_TEXT,
        verticalAlignment: "middle",
      });
    }

    const bodyText = buildBulletBlock({
      bullets: item.bullets,
      description: item.description,
    });
    if (bodyText.text) {
      shapes.push({
        id: nextId(),
        action: "create",
        ...bodyText,
        left: x + padX,
        top: y + headerH + padTop,
        width: positiveSize(colW - padX * 2, MIN_TEXT_WIDTH),
        height: bodyTextH,
        fontSize: effectiveFontSize,
        textColor: layout.textColor ?? DEFAULT_TEXT,
        fontFamily: layout.fontFamily,
      });
    }
  });

  return shapes;
}

function quadrantGridLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 4);
  while (items.length < 4) items.push({ label: "" });

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const hasAxisLabels = layout.axisLabels?.x || layout.axisLabels?.y;
  const axisMargin = hasAxisLabels ? 28 : 0;
  const gap = 6;
  const gridLeft = region.left + axisMargin;
  const gridTop = region.top;
  const gridW = region.width - axisMargin;
  const gridH = region.height - (hasAxisLabels ? 24 : 0);
  const cellW = positiveSize((gridW - gap) / 2, MIN_TEXT_WIDTH);
  const cellH = positiveSize((gridH - gap) / 2, MIN_TEXT_HEIGHT);
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const baseFontSize = layout.fontSize ?? 16;
  const normalLabelH = 30;
  const normalPad = 10;
  const compact = cellH < normalLabelH + MIN_TEXT_HEIGHT + normalPad * 2;
  const labelH = compact ? Math.min(22, Math.max(12, cellH * 0.35)) : normalLabelH;
  const pad = compact ? 6 : normalPad;
  const bodyH = positiveSize(cellH - pad * 2 - labelH, MIN_TEXT_HEIGHT);
  const bodyFontSize = compactFontSize(baseFontSize - 2, compact);

  const positions = [
    { col: 0, row: 0 },
    { col: 1, row: 0 },
    { col: 0, row: 1 },
    { col: 1, row: 1 },
  ];

  items.forEach((item, i) => {
    const { col, row } = positions[i];
    const x = gridLeft + col * (cellW + gap);
    const y = gridTop + row * (cellH + gap);
    const cellColor = usesSaturatedHeader()
      ? (item.color || accent)
      : profileSurfaceFill(accent);
    const cellTextColor = usesSaturatedHeader()
      ? (item.textColor ?? "#FFFFFF")
      : (item.textColor ?? DEFAULT_TEXT);

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: x,
      top: y,
      width: cellW,
      height: cellH,
      fillColor: cellColor,
      lineWidth: usesSaturatedHeader() ? 0 : 0.5,
      lineColor: usesSaturatedHeader() ? undefined : DEFAULT_BORDER,
    });

    if (!usesSaturatedHeader()) {
      pushAccentStrip(shapes, x, y, cellW, accent);
    }

    if (item.label) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: x + pad,
        top: y + pad,
        width: positiveSize(cellW - pad * 2, MIN_TEXT_WIDTH),
        height: labelH,
        fontSize: baseFontSize,
        bold: true,
        textColor: cellTextColor,
        fontFamily: layout.fontFamily,
        verticalAlignment: "top",
      });
    }

    const bodyText = buildBulletBlock({
      bullets: item.bullets,
      description: item.description,
    });
    if (bodyText.text) {
      shapes.push({
        id: nextId(),
        action: "create",
        ...bodyText,
        left: x + pad,
        top: y + pad + labelH,
        width: positiveSize(cellW - pad * 2, MIN_TEXT_WIDTH),
        height: bodyH,
        fontSize: bodyFontSize,
        textColor: cellTextColor,
        fontFamily: layout.fontFamily,
        verticalAlignment: "top",
      });
    }
  });

  if (layout.axisLabels?.y) {
    shapes.push({
      id: nextId(),
      action: "create",
      text: layout.axisLabels.y,
      left: region.left,
      top: gridTop + gridH / 2 - 40,
      width: axisMargin - 4,
      height: 80,
      fontSize: 10,
      textColor: "#666666",
      fontFamily: layout.fontFamily,
      verticalAlignment: "middle",
    });
  }

  if (layout.axisLabels?.x) {
    shapes.push({
      id: nextId(),
      action: "create",
      text: layout.axisLabels.x,
      left: gridLeft,
      top: gridTop + gridH + 4,
      width: gridW,
      height: 20,
      fontSize: 10,
      textColor: "#666666",
      fontFamily: layout.fontFamily,
    });
  }

  return shapes;
}

function iconTextRowsLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 6);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const gap = 16;
  const rowH = packedStride(region.height, n, gap);
  const iconW = Math.min(120, region.width * 0.2);
  const iconH = Math.min(rowH, iconW);
  const textLeft = region.left + iconW + 16;
  const textW = region.width - iconW - 16;
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const baseFontSize = layout.fontSize ?? 16;
  const saturated = usesSaturatedHeader();

  items.forEach((item, i) => {
    const y = region.top + i * (rowH + gap);
    const iconY = y + (rowH - iconH) / 2;

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "rectangle",
      left: region.left,
      top: iconY,
      width: iconW,
      height: iconH,
      fillColor: saturated ? (item.color || accent) : profileSurfaceFill(accent),
      lineWidth: saturated ? 0 : 0.5,
      lineColor: saturated ? undefined : DEFAULT_BORDER,
      text: item.label,
      textColor: saturated ? (item.textColor ?? "#FFFFFF") : (item.textColor ?? DEFAULT_TEXT),
      fontSize: baseFontSize,
      bold: item.bold ?? true,
      verticalAlignment: "middle",
    });
    if (!saturated) {
      pushAccentStrip(shapes, region.left, iconY, iconW, accent);
    }

    const bodyText = buildBulletBlock({
      bullets: item.bullets,
      description: item.description,
    });
    if (bodyText.text) {
      shapes.push({
        id: nextId(),
        action: "create",
        ...bodyText,
        left: textLeft,
        top: y,
        width: textW,
        height: rowH,
        fontSize: baseFontSize,
        textColor: layout.textColor ?? DEFAULT_TEXT,
        verticalAlignment: "middle",
      });
    }
  });

  return shapes;
}

function numberedRowsLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items.slice(0, 8);
  const n = items.length;
  if (n === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const gap = 10;
  const rowH = packedStride(region.height, n, gap);
  const numW = usesSaturatedHeader() ? 40 : 36;
  const labelW = Math.min(220, region.width * 0.25);
  const descLeft = region.left + numW + 8 + labelW + 12;
  const descW = region.width - numW - 8 - labelW - 12;
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const numColor = layout.textColor ?? "#333333";
  const baseFontSize = layout.fontSize ?? 16;
  const ruleMode = !usesSaturatedHeader();

  items.forEach((item, i) => {
    const y = region.top + i * (rowH + gap);

    if (ruleMode) {
      shapes.push({
        id: nextId(),
        action: "create",
        text: String(i + 1),
        left: region.left,
        top: y,
        width: numW,
        height: rowH,
        fontSize: Math.max(baseFontSize + 8, 24),
        bold: true,
        textColor: numColor,
        verticalAlignment: "middle",
      });

      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: region.left + numW + 8,
        top: y,
        width: labelW,
        height: rowH,
        fontSize: item.fontSize ?? baseFontSize,
        bold: item.bold ?? true,
        textColor: item.textColor ?? DEFAULT_TEXT,
        verticalAlignment: "middle",
      });

      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left,
        top: y + rowH - 1,
        width: region.width,
        height: 1,
        fillColor: numColor,
        lineWidth: 0,
      });
    } else {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "ellipse",
        left: region.left,
        top: y + (rowH - numW) / 2,
        width: numW,
        height: numW,
        fillColor: numColor,
        lineWidth: 0,
        text: String(i + 1),
        textColor: "#FFFFFF",
        fontSize: baseFontSize + 2,
        bold: true,
        verticalAlignment: "middle",
      });

      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left + numW + 8,
        top: y,
        width: labelW,
        height: rowH,
        fillColor: item.color || accent,
        lineWidth: 0,
        text: item.label,
        textColor: item.textColor ?? "#FFFFFF",
        fontSize: item.fontSize ?? baseFontSize,
        bold: item.bold ?? true,
        verticalAlignment: "middle",
      });
    }

    const bodyText = buildBulletBlock({
      bullets: item.bullets,
      description: item.description,
    });
    if (bodyText.text) {
      shapes.push({
        id: nextId(),
        action: "create",
        ...bodyText,
        left: descLeft,
        top: y,
        width: descW,
        height: rowH,
        fontSize: baseFontSize,
        textColor: layout.textColor ?? DEFAULT_TEXT,
        verticalAlignment: "middle",
      });
    }
  });

  return shapes;
}

/**
 * tableCommentary: table-like grid on the left with colored section header bars
 * and alternating data rows, plus a commentary/bullet box on the right.
 *
 * Item format:
 *  - Items with `color` set are treated as **section headers** (full-width colored bar).
 *  - Items without `color` are **data rows** (label left, description right, alternating grey/white).
 *  - The `bullets` array on items goes into the right-side commentary box.
 */
function tableCommentaryLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items;
  if (items.length === 0) return shapes;

  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const tableW = region.width * 0.72;
  const commentaryLeft = region.left + tableW + 16;
  const commentaryW = region.width - tableW - 16;
  const accent = layout.headerColor ?? "#CC0000";
  const baseFontSize = layout.fontSize ?? 16;

  const sectionItems = items.filter((it) => it.color);
  const dataItems = items.filter((it) => !it.color);
  const totalRows = items.length;
  const rowH = Math.min(36, (region.height - 30) / totalRows);
  const headerRowH = rowH;

  const commentaryBullets: string[] = [];

  let y = region.top;
  let dataIdx = 0;

  items.forEach((item) => {
    if (item.color) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left,
        top: y,
        width: tableW,
        height: headerRowH,
        fillColor: item.color || accent,
        lineWidth: 0,
        text: item.label,
        textColor: item.textColor ?? "#FFFFFF",
        fontSize: item.fontSize ?? baseFontSize,
        bold: true,
        verticalAlignment: "middle",
      });
    } else {
      const bgColor = dataIdx % 2 === 0 ? "#FFFFFF" : "#F5F5F5";
      const labelW = tableW * 0.35;
      const valuesW = tableW - labelW;

      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left,
        top: y,
        width: labelW,
        height: rowH,
        fillColor: bgColor,
        lineWidth: 0,
        text: item.label,
        textColor: DEFAULT_TEXT,
        fontSize: baseFontSize,
        verticalAlignment: "middle",
      });

      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left + labelW,
        top: y,
        width: valuesW,
        height: rowH,
        fillColor: bgColor,
        lineWidth: 0,
        text: item.description || "",
        textColor: DEFAULT_TEXT,
        fontSize: baseFontSize,
        verticalAlignment: "middle",
      });

      dataIdx++;
    }

    if (item.bullets?.length) {
      commentaryBullets.push(...item.bullets);
    }

    y += rowH;
  });

  if (commentaryBullets.length > 0) {
    shapes.push({
      id: nextId(),
      action: "create",
      ...buildBulletBlock({ bullets: commentaryBullets }),
      left: commentaryLeft,
      top: region.top,
      width: commentaryW,
      height: y - region.top,
      fontSize: baseFontSize,
      textColor: layout.textColor ?? DEFAULT_TEXT,
      verticalAlignment: "top",
    });
  }

  return shapes;
}

function bulletBoxLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const baseFontSize = layout.fontSize ?? 16;
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const items = layout.items;
  const hasHeader = items.length > 0 && items[0].label && !items[0].bullets?.length && !items[0].description;

  let headerH = 0;
  if (hasHeader) {
    headerH = compactHeaderHeight(region.height, usesSaturatedHeader() ? 36 : 22);
    const compact = headerH < 36;
    if (usesSaturatedHeader()) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left,
        top: region.top,
        width: region.width,
        height: headerH,
        fillColor: accent,
        lineWidth: 0,
        text: items[0].label,
        textColor: "#FFFFFF",
        fontSize: compactFontSize(baseFontSize, compact),
        bold: true,
        verticalAlignment: "middle",
      });
    } else {
      pushAccentStrip(shapes, region.left, region.top, region.width, accent);
      shapes.push({
        id: nextId(),
        action: "create",
        text: items[0].label,
        left: region.left,
        top: region.top + ACCENT_STRIP_HEIGHT,
        width: region.width,
        height: headerH,
        fontSize: compactFontSize(baseFontSize, compact),
        bold: true,
        textColor: DEFAULT_TEXT,
        verticalAlignment: "middle",
      });
      headerH += ACCENT_STRIP_HEIGHT;
    }
  }

  const bodyTop = region.top + headerH;
  const bodyHeight = positiveSize(region.height - headerH, MIN_TEXT_HEIGHT);
  const padY = compactPadding(bodyHeight, 12);
  const padX = padY <= 6 ? 8 : 16;
  const compactBody = padY < 12;
  const bulletFontSize = compactFontSize(baseFontSize, compactBody);

  shapes.push({
    id: nextId(),
    action: "create",
    geometricType: "rectangle",
    left: region.left,
    top: bodyTop,
    width: region.width,
    height: bodyHeight,
    fillColor: usesSaturatedHeader() ? "#F5F5F5" : profileSurfaceFill(accent),
    lineWidth: 0,
  });

  const bulletItems = hasHeader ? items.slice(1) : items;
  const bulletText = bulletTextsFromItems(bulletItems);

  if (bulletText.length > 0) {
    shapes.push({
      id: nextId(),
      action: "create",
      ...buildBulletBlock({ bullets: bulletText }),
      left: region.left + padX,
      top: bodyTop + padY,
      width: positiveSize(region.width - padX * 2, MIN_TEXT_WIDTH),
      height: positiveSize(bodyHeight - padY * 2, MIN_TEXT_HEIGHT),
      fontSize: bulletFontSize,
      textColor: layout.textColor ?? DEFAULT_TEXT,
      fontFamily: layout.fontFamily,
      verticalAlignment: "top",
    });
  }

  return shapes;
}

function splitContentLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const region = getRegion(layout, slideWidth, slideHeight);
  if (layout.title) shapes.push(titleShape(layout.title, layout, slideWidth));

  const baseFontSize = layout.fontSize ?? 16;
  const accent = layout.headerColor ?? DEFAULT_ACCENT;
  const items = layout.items;
  const gap = 14;
  const normalHeaderH = 32;
  const leftW = (region.width - gap) * 0.55;
  const rightW = region.width - leftW - gap;

  const leftItems = items.slice(0, 2);
  const rightItems = items.slice(2);

  const leftSectionH = packedStride(region.height, 2, gap);
  const compact = leftSectionH < normalHeaderH + MIN_TEXT_HEIGHT + 20;
  const saturated = usesSaturatedHeader();
  const headerH = saturated
    ? (compact ? Math.min(normalHeaderH, COMPACT_HEADER_HEIGHT) : normalHeaderH)
    : Math.max(16, Math.min(24, leftSectionH * 0.3));
  const pad = compact ? COMPACT_PADDING : 10;
  const effectiveFontSize = compactFontSize(baseFontSize, compact);

  leftItems.forEach((item, i) => {
    const y = region.top + i * (leftSectionH + gap);
    const itemColor = saturated ? (item.color || accent) : profileSurfaceFill(accent);

    if (saturated) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left,
        top: y,
        width: leftW,
        height: headerH,
        fillColor: itemColor,
        lineWidth: 0,
        text: item.label,
        textColor: "#FFFFFF",
        fontSize: baseFontSize,
        bold: true,
        verticalAlignment: "middle",
      });
    } else {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left,
        top: y,
        width: leftW,
        height: leftSectionH,
        fillColor: itemColor,
        lineWidth: 0,
      });
      pushAccentStrip(shapes, region.left, y, leftW, accent);
      shapes.push({
        id: nextId(),
        action: "create",
        text: item.label,
        left: region.left + pad,
        top: y + ACCENT_STRIP_HEIGHT + 2,
        width: positiveSize(leftW - pad * 2, MIN_TEXT_WIDTH),
        height: headerH,
        fontSize: baseFontSize,
        bold: true,
        textColor: DEFAULT_TEXT,
        verticalAlignment: "middle",
      });
    }

    const contentH = positiveSize(leftSectionH - headerH, MIN_TEXT_HEIGHT + pad * 2);
    if (saturated) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: region.left,
        top: y + headerH,
        width: leftW,
        height: contentH,
        fillColor: "#F5F5F5",
        lineWidth: 0,
      });
    }

    const bodyText = buildBulletBlock({
      bullets: item.bullets,
      description: item.description,
    });
    if (bodyText.text) {
      shapes.push({
        id: nextId(),
        action: "create",
        ...bodyText,
        left: region.left + pad,
        top: y + headerH + pad,
        width: positiveSize(leftW - pad * 2, MIN_TEXT_WIDTH),
        height: positiveSize(contentH - pad * 2, MIN_TEXT_HEIGHT),
        fontSize: effectiveFontSize,
        textColor: DEFAULT_TEXT,
        fontFamily: layout.fontFamily,
      });
    }
  });

  const rightItem = rightItems[0];
  if (rightItem) {
    const rightX = region.left + leftW + gap;
    const rightColor = saturated ? (rightItem.color || accent) : profileSurfaceFill(accent);

    if (saturated) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: rightX,
        top: region.top,
        width: rightW,
        height: headerH,
        fillColor: rightColor,
        lineWidth: 0,
        text: rightItem.label,
        textColor: "#FFFFFF",
        fontSize: baseFontSize,
        bold: true,
        verticalAlignment: "middle",
      });
    } else {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: rightX,
        top: region.top,
        width: rightW,
        height: region.height,
        fillColor: rightColor,
        lineWidth: 0,
      });
      pushAccentStrip(shapes, rightX, region.top, rightW, accent);
      shapes.push({
        id: nextId(),
        action: "create",
        text: rightItem.label,
        left: rightX + pad,
        top: region.top + ACCENT_STRIP_HEIGHT + 2,
        width: positiveSize(rightW - pad * 2, MIN_TEXT_WIDTH),
        height: headerH,
        fontSize: baseFontSize,
        bold: true,
        textColor: DEFAULT_TEXT,
        verticalAlignment: "middle",
      });
    }

    const rightBodyH = positiveSize(region.height - headerH, MIN_TEXT_HEIGHT + pad * 2);
    if (saturated) {
      shapes.push({
        id: nextId(),
        action: "create",
        geometricType: "rectangle",
        left: rightX,
        top: region.top + headerH,
        width: rightW,
        height: rightBodyH,
        fillColor: "#F5F5F5",
        lineWidth: 0,
      });
    }

    const allBullets = [
      ...(rightItem.bullets || []),
      ...bulletTextsFromItems(rightItems.slice(1)),
    ];
    if (allBullets.length > 0) {
      shapes.push({
        id: nextId(),
        action: "create",
        ...buildBulletBlock({ bullets: allBullets }),
        left: rightX + pad,
        top: region.top + headerH + pad,
        width: positiveSize(rightW - pad * 2, MIN_TEXT_WIDTH),
        height: positiveSize(rightBodyH - pad * 2, MIN_TEXT_HEIGHT),
        fontSize: effectiveFontSize,
        textColor: DEFAULT_TEXT,
        fontFamily: layout.fontFamily,
      });
    }
  }

  return shapes;
}

// ---------------------------------------------------------------------------
// Citi-branded snippets (COM only, gated by citiStyle flag)
// ---------------------------------------------------------------------------

const CITI_BLUE = `#${CITI_COLORS.citiBlue}`;
const CITI_INK = `#${CITI_COLORS.citiInk}`;
const CITI_GRAY01 = `#${CITI_COLORS.gray01}`;
const CITI_GRAY04 = `#${CITI_COLORS.gray04}`;
const CITI_SAND04 = `#${CITI_COLORS.sand04}`;
const CITI_WHITE = `#${CITI_COLORS.white}`;
const CITI_FONT = CITI_THEME_COLORS.minorFont;
const CITI_FONT_DISPLAY = CITI_THEME_COLORS.majorFont;
const CITI_FONT_CONDENSED = "Citi Sans Condensed";

const CITI_TITLE_LEFT = CITI_POSITIONS.title.left;
const CITI_TITLE_TOP = CITI_POSITIONS.title.top;
const CITI_TITLE_WIDTH = CITI_POSITIONS.title.width;
const CITI_TITLE_HEIGHT = CITI_POSITIONS.title.height;
const CITI_TITLE_FONT_SIZE = 22;

const CITI_CONTENT_TOP = CITI_POSITIONS.contentStart.top;
const CITI_CONTENT_LEFT = CITI_POSITIONS.contentStart.left;
const CITI_FOOTER_TOP = CITI_POSITIONS.footer.top;

function citiTitleShape(title: string, snippet: Snippet): ShapeModification {
  return {
    id: nextId(),
    action: "create",
    text: title,
    left: snippet.left ?? CITI_TITLE_LEFT,
    top: CITI_TITLE_TOP,
    width: snippet.width ?? CITI_TITLE_WIDTH,
    height: CITI_TITLE_HEIGHT,
    fontSize: CITI_TITLE_FONT_SIZE,
    bold: false,
    textColor: CITI_BLUE,
    fontFamily: CITI_FONT_DISPLAY,
    verticalAlignment: "bottom",
  };
}


function getCitiRegion(snippet: Snippet, slideWidth: number, _slideHeight?: number): Region {
  void _slideHeight;
  return {
    left: snippet.left ?? CITI_CONTENT_LEFT,
    top: snippet.top ?? CITI_CONTENT_TOP,
    width: snippet.width ?? (slideWidth - (snippet.left ?? CITI_CONTENT_LEFT) * 2),
    height: snippet.height ?? (CITI_FOOTER_TOP - (snippet.top ?? CITI_CONTENT_TOP) - 20),
  };
}

/**
 * Citi text table — header row in Citi blue fill with white text, left column
 * in Citi ink fill with white text, body cells in alternating white/gray.
 *
 * Item encoding:
 *   - First item → column headers (label = left header, description = pipe-separated)
 *   - Remaining items → data rows (label = row header, description = pipe-separated values)
 *   - `bold: true` → highlight row (Sand 04 background, Citi blue text)
 */
function citiTextTableLayout(
  layout: Snippet,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const shapes: ShapeModification[] = [];
  const items = layout.items;
  if (items.length < 2) return shapes;

  const region = getCitiRegion(layout, slideWidth, slideHeight);
  const baseFontSize = layout.fontSize ?? 10;
  const font = layout.fontFamily ?? CITI_FONT;

  if (layout.title) {
    shapes.push(citiTitleShape(layout.title, layout));
  }

  const headerItem = items[0];
  const dataItems = items.slice(1);
  const colHeaders = headerItem.description ? headerItem.description.split("|") : [];
  const leftColW = region.width * 0.24;
  const dataColW = colHeaders.length > 0 ? (region.width - leftColW) / colHeaders.length : 0;
  const rowH = Math.min(26, region.height / items.length);

  let y = region.top;

  // Header row — Citi Blue fill, white text
  shapes.push({
    id: nextId(), action: "create", geometricType: "rectangle",
    left: region.left, top: y, width: leftColW, height: rowH,
    fillColor: CITI_BLUE, lineColor: CITI_BLUE, lineWidth: 0.25,
    text: headerItem.label, textColor: CITI_WHITE,
    fontSize: baseFontSize, bold: true, fontFamily: font,
    verticalAlignment: "middle",
  });
  colHeaders.forEach((h, ci) => {
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      left: region.left + leftColW + ci * dataColW, top: y,
      width: dataColW, height: rowH,
      fillColor: CITI_BLUE, lineColor: CITI_BLUE, lineWidth: 0.25,
      text: h.trim(), textColor: CITI_WHITE,
      fontSize: baseFontSize, bold: true, fontFamily: font,
      verticalAlignment: "middle",
    });
  });
  y += rowH;

  // Top blue line
  shapes.push({
    id: nextId(), action: "create", geometricType: "line",
    left: region.left, top: y, width: region.width, height: 0,
    lineColor: CITI_BLUE, lineWidth: 0.75,
  });

  // Data rows
  dataItems.forEach((item, ri) => {
    const vals = item.description ? item.description.split("|") : [];
    const isHighlight = item.bold === true;
    const rowBg = isHighlight ? CITI_SAND04 : CITI_WHITE;
    const textCol = isHighlight ? CITI_BLUE : CITI_INK;

    // Row separator (thin grey line between rows)
    if (ri > 0) {
      shapes.push({
        id: nextId(), action: "create", geometricType: "line",
        left: region.left, top: y, width: region.width, height: 0,
        lineColor: CITI_GRAY04, lineWidth: 0.25,
      });
    }

    // Row label cell
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      left: region.left, top: y, width: leftColW, height: rowH,
      fillColor: rowBg, lineColor: rowBg, lineWidth: 0.25,
      text: item.label, textColor: textCol,
      fontSize: baseFontSize, bold: isHighlight, fontFamily: font,
      verticalAlignment: "middle",
    });

    // Value cells
    vals.forEach((val, ci) => {
      shapes.push({
        id: nextId(), action: "create", geometricType: "rectangle",
        left: region.left + leftColW + ci * dataColW, top: y,
        width: dataColW, height: rowH,
        fillColor: rowBg, lineColor: rowBg, lineWidth: 0.25,
        text: val.trim(), textColor: textCol,
        fontSize: baseFontSize, bold: isHighlight, fontFamily: font,
        verticalAlignment: "middle",
      });
    });

    y += rowH;
  });

  // Bottom blue line
  shapes.push({
    id: nextId(), action: "create", geometricType: "line",
    left: region.left, top: y, width: region.width, height: 0,
    lineColor: CITI_BLUE, lineWidth: 0.75,
  });

  return shapes;
}

/**
 * Citi quadrant — 2x2 content grid matching the Citi template layout.
 * Each item becomes one quadrant cell with a header (12pt bold Citi blue)
 * and body content (bullets or description text).
 *
 * Items are ordered: top-left, top-right, bottom-left, bottom-right.
 * Set `color` on an item to give it a Sand 04 fill (description panel style).
 */
function citiQuadrantLayout(
  layout: Snippet,
  slideWidth: number,
  _slideHeight: number
): ShapeModification[] {
  void _slideHeight;
  const shapes: ShapeModification[] = [];
  const items = layout.items;
  if (items.length === 0) return shapes;

  const font = layout.fontFamily ?? CITI_FONT;
  const baseFontSize = layout.fontSize ?? 10;

  if (layout.title) {
    shapes.push(citiTitleShape(layout.title, layout));
  }

  const colGap = 17.2;
  const rowGap = 14.2;
  const gridLeft = CITI_CONTENT_LEFT;
  const gridTop = CITI_CONTENT_TOP;
  const totalWidth = (layout.width ?? (slideWidth - gridLeft * 2));
  const totalHeight = CITI_FOOTER_TOP - gridTop - 20;
  const cellW = (totalWidth - colGap) / 2;
  const cellH = (totalHeight - rowGap) / 2;
  const headerH = 24;

  const positions = [
    { left: gridLeft, top: gridTop },
    { left: gridLeft + cellW + colGap, top: gridTop },
    { left: gridLeft, top: gridTop + cellH + rowGap },
    { left: gridLeft + cellW + colGap, top: gridTop + cellH + rowGap },
  ];

  items.slice(0, 4).forEach((item, i) => {
    const pos = positions[i];
    const fullText = buildBulletBlock({
      label: item.label,
      bullets: item.bullets,
      description: item.description,
    });

    pushGeometricTextBlock(
      shapes,
      {
        geometricType: "roundedRectangle",
        left: pos.left,
        top: pos.top,
        width: cellW,
        height: cellH,
        fillColor: CITI_WHITE,
        lineColor: CITI_GRAY04,
        lineWidth: 0.5,
      },
      fullText,
      {
        textColor: CITI_BLUE,
        fontSize: baseFontSize,
        fontFamily: font,
        verticalAlignment: "top",
      },
      { inset: 10 }
    );
  });

  return shapes;
}

/**
 * Citi split content — two-column layout from the Citi template.
 * Left column (12.8, 131.1, 368.5×365.6): heading + bullet content
 * Right column (398.3, 131.1, 368.5×365.6): heading + subheading + table rows
 *
 * Item encoding:
 *   items[0] = left panel (label = heading, bullets/description = body,
 *              color set → sand fill background)
 *   items[1] = right panel header (label = table heading in Citi Blue 12pt bold,
 *              description = subheading in Citi Ink 11pt bold,
 *              bullets = column headers rendered in Citi Blue above data)
 *   items[2+] = data rows (label = row label, description = pipe-separated values,
 *               bold = true → highlight row with Sand 04 fill + Citi Blue text)
 */
function citiSplitContentLayout(
  layout: Snippet,
  slideWidth: number,
  _slideHeight: number
): ShapeModification[] {
  void _slideHeight;
  const shapes: ShapeModification[] = [];
  const items = layout.items;
  if (items.length < 2) return shapes;

  const font = layout.fontFamily ?? CITI_FONT;
  const baseFontSize = layout.fontSize ?? 10;

  if (layout.title) {
    shapes.push(citiTitleShape(layout.title, layout));
  }

  const colGap = 17.2;
  const panelLeft = CITI_CONTENT_LEFT;
  const panelTop = CITI_CONTENT_TOP;
  const totalWidth = layout.width ?? (slideWidth - panelLeft * 2);
  const panelH = CITI_FOOTER_TOP - panelTop - 20;
  const colW = (totalWidth - colGap) / 2;

  const leftItem = items[0];
  const rightItem = items[1];
  const rightRows = items.slice(2);

  // --- Left panel (single rounded rect with heading + bullets inside) ---
  const leftFill = leftItem.color ? CITI_SAND04 : CITI_WHITE;
  const leftText = buildBulletBlock({
    label: leftItem.label,
    bullets: leftItem.bullets,
    description: leftItem.description,
  });

  pushGeometricTextBlock(
    shapes,
    {
      geometricType: "roundedRectangle",
      left: panelLeft,
      top: panelTop,
      width: colW,
      height: panelH,
      fillColor: leftFill,
      lineColor: leftItem.color ? leftFill : CITI_GRAY04,
      lineWidth: 0.5,
    },
    leftText,
    {
      textColor: CITI_BLUE,
      fontSize: baseFontSize,
      fontFamily: font,
      verticalAlignment: "top",
    },
    { inset: 14 }
  );

  // --- Right panel ---
  const rightLeft = panelLeft + colW + colGap;

  // Table heading (12pt Citi Blue bold)
  shapes.push({
    id: nextId(), action: "create", geometricType: "rectangle",
    left: rightLeft, top: panelTop,
    width: colW, height: 20,
    fillColor: CITI_WHITE, lineColor: CITI_WHITE, lineWidth: 0.25,
    text: rightItem.label,
    textColor: CITI_BLUE,
    fontSize: 12, bold: true, fontFamily: font,
    verticalAlignment: "top",
  });

  // Table subheading (11pt Citi Ink bold)
  let tableTop = panelTop + 22;
  if (rightItem.description) {
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      left: rightLeft, top: tableTop,
      width: colW, height: 16,
      fillColor: CITI_WHITE, lineColor: CITI_WHITE, lineWidth: 0.25,
      text: rightItem.description,
      textColor: CITI_INK,
      fontSize: 11, bold: true, fontFamily: font,
      verticalAlignment: "top",
    });
    tableTop += 20;
  }

  // Column headers (from rightItem.bullets) — rendered in Citi Blue
  const colHeaders = rightItem.bullets || [];
  const hasHeaders = colHeaders.length > 0;
  const labelW = colW * 0.4;
  const dataW = colW - labelW;
  const numDataCols = colHeaders.length > 0
    ? colHeaders.length
    : (rightRows[0]?.description?.split("|").length || 2);
  const valColW = dataW / numDataCols;

  if (hasHeaders) {
    colHeaders.forEach((hdr, ci) => {
      shapes.push({
        id: nextId(), action: "create", geometricType: "rectangle",
        left: rightLeft + labelW + ci * valColW, top: tableTop,
        width: valColW, height: 18,
        fillColor: CITI_WHITE, lineColor: CITI_WHITE, lineWidth: 0.25,
        text: hdr, textColor: CITI_BLUE,
        fontSize: baseFontSize, fontFamily: font,
        verticalAlignment: "bottom",
      });
    });
    tableTop += 20;
  }

  // Data rows
  if (rightRows.length > 0) {
    const availH = panelTop + panelH - tableTop - 10;
    const rowH = Math.min(24, availH / rightRows.length);

    // Top blue line
    shapes.push({
      id: nextId(), action: "create", geometricType: "line",
      left: rightLeft, top: tableTop, width: colW, height: 0,
      lineColor: CITI_BLUE, lineWidth: 0.75,
    });

    let y = tableTop;
    rightRows.forEach((row, ri) => {
      const isHighlight = row.bold === true;
      const bgColor = isHighlight ? CITI_SAND04 : CITI_WHITE;
      const textCol = isHighlight ? CITI_BLUE : CITI_INK;
      const cols = row.description ? row.description.split("|") : [];

      if (ri > 0) {
        shapes.push({
          id: nextId(), action: "create", geometricType: "line",
          left: rightLeft, top: y, width: colW, height: 0,
          lineColor: CITI_GRAY04, lineWidth: 0.25,
        });
      }

      // Row label (left-aligned)
      shapes.push({
        id: nextId(), action: "create", geometricType: "rectangle",
        left: rightLeft, top: y, width: labelW, height: rowH,
        fillColor: bgColor, lineColor: bgColor, lineWidth: 0.25,
        text: row.label, textColor: textCol,
        fontSize: baseFontSize, bold: isHighlight, fontFamily: font,
        verticalAlignment: "middle",
      });

      // Row values (center-aligned)
      cols.forEach((val, ci) => {
        shapes.push({
          id: nextId(), action: "create", geometricType: "rectangle",
          left: rightLeft + labelW + ci * valColW, top: y,
          width: valColW, height: rowH,
          fillColor: bgColor, lineColor: bgColor, lineWidth: 0.25,
          text: val.trim(), textColor: textCol,
          fontSize: baseFontSize, bold: isHighlight, fontFamily: font,
          verticalAlignment: "middle",
        });
      });

      y += rowH;
    });

    // Bottom blue line
    shapes.push({
      id: nextId(), action: "create", geometricType: "line",
      left: rightLeft, top: y, width: colW, height: 0,
      lineColor: CITI_BLUE, lineWidth: 0.75,
    });
  } else if (rightItem.bullets?.length) {
    shapes.push({
      id: nextId(), action: "create",
      left: rightLeft, top: tableTop,
      width: colW, height: panelH - (tableTop - panelTop),
      ...buildBulletBlock({ bullets: rightItem.bullets }),
      textColor: CITI_INK,
      fontSize: baseFontSize, fontFamily: font,
      verticalAlignment: "top",
      wordWrap: true,
    });
  }

  return shapes;
}

/**
 * Citi tombstone page — grid of rounded-rectangle deal cards.
 * Matches the Citi template: 6 columns, auto-rows, cards 112.1×113.4 with 13pt gap.
 *
 * Item encoding:
 *   - label = deal description (e.g. client name or deal headline)
 *   - description = pipe-separated lines below description: "deal size|date"
 *   - color controls card style:
 *       "#255BE3" (Citi Blue) → highlighted deal (blue fill, white text)
 *       "#E6EBED" (Gray 04) → gray tombstone
 *       "#EAE0D3" (Sand 04) → sand tombstone
 *       unset / "#FFFFFF"    → plain white card with gray outline
 *   - bold = true → deal size line rendered bold
 */
function citiTombstoneLayout(
  layout: Snippet,
  slideWidth: number,
  _slideHeight: number
): ShapeModification[] {
  void _slideHeight;
  const shapes: ShapeModification[] = [];
  const items = layout.items;
  if (items.length === 0) return shapes;

  const font = layout.fontFamily ?? CITI_FONT;

  if (layout.title) {
    shapes.push(citiTitleShape(layout.title, layout));
  }

  const maxCols = 6;
  const gridLeft = layout.left ?? CITI_TITLE_LEFT;
  const gridTop = layout.top ?? CITI_CONTENT_TOP;
  const availableW = (layout.width ?? slideWidth) - gridLeft * 2;
  const availableH = CITI_FOOTER_TOP - gridTop - 20;
  const numRows = Math.min(3, Math.ceil(items.length / maxCols));

  const gapRatio = 0.115;
  const cardW = availableW / (maxCols + (maxCols - 1) * gapRatio);
  const gapX = cardW * gapRatio;
  const cardH = (availableH - (numRows - 1) * gapX) / numRows;
  const gapY = gapX;

  items.slice(0, maxCols * 3).forEach((item, i) => {
    const col = i % maxCols;
    const row = Math.floor(i / maxCols);
    const cx = gridLeft + col * (cardW + gapX);
    const cy = gridTop + row * (cardH + gapY);

    const rawColor = (item.color || "").toUpperCase().replace("#", "");
    const isHighlight = rawColor === "255BE3";
    const isGray = rawColor === "E6EBED";
    const isSand = rawColor === "EAE0D3";

    const fillColor = isHighlight ? CITI_BLUE
      : isGray ? CITI_GRAY04
      : isSand ? CITI_SAND04
      : CITI_WHITE;
    const textColor = isHighlight ? CITI_WHITE : CITI_INK;
    const lineColor = (isHighlight || isGray || isSand) ? undefined : CITI_GRAY01;
    const lineWidth = lineColor ? 0.5 : 0;

    const descLines = item.description ? item.description.split("|") : [];
    const dealSize = descLines[0] || "";
    const dealDate = descLines[1] || "";

    const cardText = [
      item.label || "",
      "\n",
      dealSize,
      dealDate,
    ].join("\n");

    shapes.push({
      id: nextId(),
      action: "create",
      geometricType: "roundedRectangle",
      left: cx,
      top: cy,
      width: cardW,
      height: cardH,
      fillColor,
      lineColor,
      lineWidth,
      text: cardText,
      textColor,
      fontSize: 8,
      bold: item.bold ?? false,
      fontFamily: font,
      verticalAlignment: "middle",
    });
  });

  return shapes;
}

/**
 * Citi matrix — header row + left column matrix table matching the Citi template.
 * Blue header row (column headings), blue left column (row headings),
 * body cells with text content. Sand 04 highlight on rows with `bold: true`.
 *
 * Item encoding:
 *   - Items[0] = column headers (label = top-left corner, description = pipe-separated headers)
 *   - Items[1+] = data rows (label = row heading, description = pipe-separated cell content)
 *   - `bold: true` on a row → Sand 04 fill (highlighted row)
 *   - Use `\n` within cell text for sub-bullets
 */
function citiMatrixLayout(
  layout: Snippet,
  slideWidth: number,
  _slideHeight: number
): ShapeModification[] {
  void _slideHeight;
  const shapes: ShapeModification[] = [];
  const items = layout.items;
  if (items.length < 2) return shapes;

  const font = layout.fontFamily ?? CITI_FONT;
  const baseFontSize = layout.fontSize ?? 10;

  if (layout.title) {
    shapes.push(citiTitleShape(layout.title, layout));
  }

  const margin = layout.left ?? CITI_TITLE_LEFT;
  const tableLeft = margin;
  const tableTop = layout.top ?? CITI_CONTENT_TOP;
  const tableW = layout.width ?? (slideWidth - margin * 2);
  const tableH = CITI_FOOTER_TOP - tableTop - 20;

  const headerItem = items[0];
  const dataRows = items.slice(1);
  const colHeaders = headerItem.description ? headerItem.description.split("|") : [];
  const numCols = colHeaders.length + 1;
  const numRows = dataRows.length + 1;

  const rowHeaderW = tableW * 0.18;
  const colW = (tableW - rowHeaderW) / Math.max(colHeaders.length, 1);
  const headerRowH = 32;
  const dataRowH = (tableH - headerRowH) / Math.max(dataRows.length, 1);

  // Header row — column headings (skip top-left corner)
  if (headerItem.label) {
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      left: tableLeft, top: tableTop, width: rowHeaderW, height: headerRowH,
      fillColor: CITI_WHITE, lineWidth: 0,
      text: headerItem.label, textColor: CITI_INK,
      fontSize: baseFontSize, fontFamily: font,
      verticalAlignment: "middle",
    });
  }

  colHeaders.forEach((h, ci) => {
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      left: tableLeft + rowHeaderW + ci * colW, top: tableTop,
      width: colW, height: headerRowH,
      fillColor: CITI_BLUE, lineWidth: 0,
      text: h.trim(), textColor: CITI_WHITE,
      fontSize: baseFontSize, bold: true, fontFamily: font,
      verticalAlignment: "middle",
    });
  });

  // Data rows
  dataRows.forEach((row, ri) => {
    const y = tableTop + headerRowH + ri * dataRowH;
    const isHighlight = row.bold === true;
    const cellBg = isHighlight ? CITI_SAND04 : CITI_WHITE;

    // Row heading — blue fill, white text
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      left: tableLeft, top: y, width: rowHeaderW, height: dataRowH,
      fillColor: CITI_BLUE, lineWidth: 0,
      text: row.label, textColor: CITI_WHITE,
      fontSize: baseFontSize, bold: true, fontFamily: font,
      verticalAlignment: "middle",
    });

    // Data cells
    const cellValues = row.description ? row.description.split("|") : [];
    colHeaders.forEach((_h, ci) => {
      const cellText = (cellValues[ci] || "").trim();
      shapes.push({
        id: nextId(), action: "create", geometricType: "rectangle",
        left: tableLeft + rowHeaderW + ci * colW, top: y,
        width: colW, height: dataRowH,
        fillColor: cellBg, lineColor: CITI_GRAY04, lineWidth: 0.25,
        text: cellText, textColor: CITI_INK,
        fontSize: baseFontSize, fontFamily: font,
        verticalAlignment: "top",
      });
    });

    // Row separator line
    shapes.push({
      id: nextId(), action: "create", geometricType: "line",
      left: tableLeft, top: y + dataRowH, width: tableW, height: 0,
      lineColor: CITI_BLUE, lineWidth: 0.5,
    });
  });

  return shapes;
}

/**
 * Citi title slide — cover page with business name banner, large title,
 * subtitle, date/confidentiality line, and a small blue accent bar.
 *
 * Item encoding:
 *   - items[0].label → subtitle text (18pt)
 *   - items[0].description → business name line (e.g. "Citi Business Name  |  Subgroup Name")
 *   - items[1].label → date/confidentiality line (e.g. "Date  |  Strictly private and confidential")
 *   - items[2].label → disclaimer paragraph 1 (bold)
 *   - items[2].description → disclaimer paragraph 2 (regular)
 */
function citiTitleSlideLayout(
  layout: Snippet,
  slideWidth: number,
  _slideHeight: number
): ShapeModification[] {
  void _slideHeight;
  const shapes: ShapeModification[] = [];
  const title = layout.title || "Presentation title";
  const margin = 33.2;
  const contentW = slideWidth - margin * 2;

  const subtitleItem = layout.items?.[0];
  const dateItem = layout.items?.[1];
  const disclaimerItem = layout.items?.[2];

  const subtitle = subtitleItem?.label || "";
  const businessName = subtitleItem?.description || "";
  const dateLine = dateItem?.label || "";

  shapes.push({
    id: nextId(), action: "create", geometricType: "rectangle",
    left: 21.6, top: 7.2, width: 56.9, height: 10.1,
    fillColor: CITI_BLUE, fontFamily: CITI_FONT, fontSize: 8,
  });

  if (businessName) {
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      text: businessName,
      left: margin, top: 29.7, width: contentW, height: 14.5,
      fillColor: CITI_WHITE, lineColor: CITI_WHITE, lineWidth: 0.01,
      fontSize: 12, fontFamily: CITI_FONT, textColor: CITI_INK,
    });
  }

  shapes.push({
    id: nextId(), action: "create", geometricType: "rectangle",
    text: title,
    left: margin, top: 214.9, width: contentW, height: 56.7,
    fillColor: CITI_WHITE, lineColor: CITI_WHITE, lineWidth: 0.01,
    fontSize: 36, fontFamily: CITI_FONT_DISPLAY, textColor: CITI_BLUE,
    bold: false, verticalAlignment: "bottom",
  });

  if (subtitle) {
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      text: subtitle,
      left: margin, top: 289.8, width: contentW, height: 28.3,
      fillColor: CITI_WHITE, lineColor: CITI_WHITE, lineWidth: 0.01,
      fontSize: 18, fontFamily: CITI_FONT, textColor: CITI_INK,
    });
  }

  if (disclaimerItem) {
    const parts: string[] = [];
    if (disclaimerItem.label) parts.push(disclaimerItem.label);
    if (disclaimerItem.description) parts.push(disclaimerItem.description);
    if (parts.length > 0) {
      shapes.push({
        id: nextId(), action: "create", geometricType: "rectangle",
        text: parts.join("\r"),
        left: margin, top: 401.6, width: contentW, height: 60.6,
        fillColor: CITI_WHITE, lineColor: CITI_WHITE, lineWidth: 0.01,
        fontSize: 10, fontFamily: CITI_FONT, textColor: CITI_INK,
        bold: true,
      });
    }
  }

  if (dateLine) {
    shapes.push({
      id: nextId(), action: "create", geometricType: "rectangle",
      text: dateLine,
      left: slideWidth - margin - 174.9, top: 494.9, width: 174.9, height: 12.1,
      fillColor: CITI_WHITE, lineColor: CITI_WHITE, lineWidth: 0.01,
      fontSize: 10, fontFamily: CITI_FONT, textColor: CITI_INK,
    });
  }

  return shapes;
}

const layoutGenerators: Record<
  string,
  (layout: Snippet, w: number, h: number) => ShapeModification[]
> = {
  processFlow: processFlowLayout,
  // funnel: funnelLayout,
  // pyramid: pyramidLayout,
  // cycleDiagram: cycleDiagramLayout,
  kpiDashboard: kpiDashboardLayout,
  orgChart: orgChartLayout,
  // matrixGrid: matrixGridLayout,
  comparisonColumns: comparisonColumnsLayout,
  // waterfallBreakdown: waterfallBreakdownLayout,
  // iconStatRow: iconStatRowLayout,
  // swimLanes: swimLanesLayout,
  // beforeAfter: beforeAfterLayout,
  // chevronBanner: chevronBannerLayout,
  statGrid: statGridLayout,
  sectionBlock: sectionBlockLayout,
  columnCards: columnCardsLayout,
  quadrantGrid: quadrantGridLayout,
  iconTextRows: iconTextRowsLayout,
  numberedRows: numberedRowsLayout,
  bulletBox: bulletBoxLayout,
  splitContent: splitContentLayout,
  // tableCommentary: tableCommentaryLayout,
  citiTextTable: citiTextTableLayout,
  citiQuadrant: citiQuadrantLayout,
  citiSplitContent: citiSplitContentLayout,
  citiTombstone: citiTombstoneLayout,
  citiMatrix: citiMatrixLayout,
  // citiTitleSlide: citiTitleSlideLayout,
};

export function generateSnippetShapes(
  snippet: Snippet,
  slideWidth: number,
  slideHeight: number,
  themeAccent?: string,
  options?: {
    useTextBoxesForStatGrid?: boolean;
    styleProfile?: SnippetStyleProfile;
  }
): ShapeModification[] {
  _idCounter = 0;
  const accentBase = snippet.headerColor || themeAccent || DEFAULT_ACCENT;
  _themeAccent = accentBase;
  _useTextBoxesForStatGrid = options?.useTextBoxesForStatGrid ?? false;
  _snippetProfile = options?.styleProfile ?? DEFAULT_SNIPPET_STYLE_PROFILE;

  if (!snippet.headerColor) {
    snippet = { ...snippet, headerColor: accentBase };
  }

  const items = snippet.items;
  if (items.length > 0 && _snippetProfile.shadeItemsFromHeader) {
    const shades = accentShades(accentBase, items.length);
    snippet = {
      ...snippet,
      items: items.map((item, i) => ({ ...item, color: item.color ?? shades[i] })),
    };
  }

  const generator = layoutGenerators[snippet.type];
  if (!generator) {
    console.warn(`[SNIPPET] Unknown type: ${snippet.type}`);
    return [];
  }
  return sanitizeExpandedShapes(generator(snippet, slideWidth, slideHeight));
}

/** @deprecated Use generateSnippetShapes */
export const generateCompositeLayoutShapes = generateSnippetShapes;

export function isSnippetType(type: string): boolean {
  return type in layoutGenerators;
}

/** @deprecated Use isSnippetType */
export const isCompositeLayoutType = isSnippetType;
